import React from 'react';
import { Input as FluentInput, InputProps as FluentInputProps } from '@fluentui/react-components';
import { isAlphanumericOrSpace, isNumericOnly, sanitizeText, sanitizeTel } from '#root/utils/validation';

export type InputType = 'text' | 'tel' | 'email' | 'password' | 'unrestricted';

export interface InputProps extends Omit<FluentInputProps, 'type'> {
  type?: InputType;
}

export const Input: React.FC<InputProps> = ({ type = 'text', onChange, onKeyDown, ...props }) => {

  const sanitizeInput = (value: string): string => {
    if (!value) return value;
    
    switch (type) {
      case 'text':
        return sanitizeText(value);
      case 'tel':
        return sanitizeTel(value);
      case 'email':
      case 'password':
      case 'unrestricted':
      default:
        return value;
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    // Skip sanitization for unrestricted types
    if (type !== 'text' && type !== 'tel') return;

    // Get pasted content and sanitize if needed
    const pastedText = e.clipboardData.getData('text');
    const sanitizedText = sanitizeInput(pastedText);
    
    if (pastedText === sanitizedText) return; // If no sanitization was needed, let default paste behavior work
    
    // Get current value and selection
    const input = e.currentTarget;
    const currentValue = input.value;
    const selectionStart = input.selectionStart || 0;
    const selectionEnd = input.selectionEnd || 0;
    
    // Create new value by splicing in the sanitized text
    const beforePaste = currentValue.substring(0, selectionStart);
    const afterPaste = currentValue.substring(selectionEnd);
    const newValue = beforePaste + sanitizedText + afterPaste;
    
    e.preventDefault();
    
    // Use FluentUI's onChange handler
    onChange?.(e as any, { value: newValue });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Allow custom keydown handler to run first
    onKeyDown?.(e);
    if (e.defaultPrevented) return;

    // Allow Ctrl/Cmd key combinations for copy, paste, cut, etc.
    if (e.ctrlKey || e.metaKey) return;

    // Skip restriction for special keys
    const specialKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'];
    if (specialKeys.includes(e.key)) return;

    switch (type) {
      case 'text':
        // Only allow alphanumeric and space
        if (!isAlphanumericOrSpace(e.key)) {
          e.preventDefault();
        }
        break;
      case 'tel':
        // Only allow numbers and space
        if (!isNumericOnly(e.key) && e.key !== ' ') {
          e.preventDefault();
        }
        break;
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>, data: { value: string }) => {
    
    const sanitizedValue = sanitizeInput(data.value);
    onChange?.(e, { ...data, value: sanitizedValue });
  };

  return (
    <FluentInput
      {...props}
      type={type === 'unrestricted' ? 'text' : type}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      onPaste={handlePaste}
    />
  );
};
